import { IMAGE_CONFIG } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorDefaultOptions } from '@angular/material/paginator';
import { DomSanitizer } from '@angular/platform-browser';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { environment } from '../environments/environment';
import { provideApi } from './api/provide-api';
import { routes } from './app-routing';
import { AuthenticationService } from './auth/authentication.service';
import {
  DEFAULT_LANGUAGE,
  httpLanguageInterceptor,
  LanguageService,
  SUPPORTED_LANGUAGES,
  TranslocoJsLoader,
} from './locales';
import { CustomTitleStrategy } from './title-strategy';

function initApplication(
  languageService: LanguageService,
  translocoService: TranslocoService,
  _: AuthenticationService, // it just needs to be loaded so the constructor effects trigger
  iconRegistry: MatIconRegistry,
  sanitizer: DomSanitizer,
) {
  return () => {
    iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
    iconRegistry.addSvgIconResolver((name, namespace) => {
      if (namespace === '') {
        return sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/${name}.svg`);
      }
      console.error(`Unknown icon: ${namespace} ${name}`);
      return null;
    });
    const lang = languageService.language();
    translocoService.setActiveLang(lang);
    const promises = [firstValueFrom(translocoService.load(lang))];
    return Promise.all(promises);
  };
}

/**
 * Providers shared by the browser app (main.ts) and the build-time app shell render (main.server.ts).
 * Firebase Auth and other browser-only providers are added by each entry point.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideTransloco({
      config: {
        availableLangs: SUPPORTED_LANGUAGES,
        defaultLang: DEFAULT_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoJsLoader,
    }),
    {
      provide: LOCALE_ID,
      deps: [LanguageService],
      useFactory: (languageService: LanguageService) => languageService.getCurrentLocale(),
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAppInitializer(() => {
      const initializerFn = initApplication(
        inject(LanguageService),
        inject(TranslocoService),
        inject(AuthenticationService),
        inject(MatIconRegistry),
        inject(DomSanitizer),
      );
      return initializerFn();
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        showFirstLastButtons: true,
        pageSizeOptions: [10, 20, 50, 100, 200, 500],
        pageSize: 50,
      } satisfies MatPaginatorDefaultOptions,
    },
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withComponentInputBinding(),
      withViewTransitions({ skipInitialTransition: true }),
    ),
    provideHttpClient(withInterceptors([httpLanguageInterceptor])),
    provideZonelessChangeDetection(),
    { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true, disableImageLazyLoadWarning: true } },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideApi({ basePath: '' }),
  ],
};
